/**
 * Cart Page Product Recommendations
 * Uses Shopify Section Rendering API to get real product-grid-item cards.
 * After injection: unlazies rimg images, wires up ATC via AddToCartFlyout.
 */
(function () {
  'use strict';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    var container = document.querySelector('[data-cart-recommendations]');
    if (!container) return;

    var sliderEl = container.querySelector('[data-cart-recs-slider]');
    if (!sliderEl) return;

    var slidesDesktop = parseInt(sliderEl.dataset.slidesDesktop, 10) || 4;
    var slidesMobile = parseInt(sliderEl.dataset.slidesMobile, 10) || 2;
    var limit = parseInt(sliderEl.dataset.limit, 10) || 10;

    fetch('/cart.js')
      .then(function (r) {
        if (!r.ok) throw new Error('Cart fetch failed');
        return r.json();
      })
      .then(function (cart) {
        if (!cart.items || cart.items.length === 0) {
          container.style.display = 'none';
          return;
        }

        var cartProductIds = [];
        cart.items.forEach(function (item) {
          var pid = String(item.product_id);
          if (cartProductIds.indexOf(pid) === -1) {
            cartProductIds.push(pid);
          }
        });

        fetchSectionRecommendations(cartProductIds, sliderEl, container, limit, slidesDesktop, slidesMobile);
      })
      .catch(function () {
        container.style.display = 'none';
      });
  }

  function fetchSectionRecommendations(cartProductIds, sliderEl, container, limit, slidesDesktop, slidesMobile) {
    var sectionId = 'cart-recommendations';
    var perProduct = Math.ceil(limit / cartProductIds.length);
    if (perProduct < 4) perProduct = 4;

    var promises = cartProductIds.map(function (pid) {
      var url = '/recommendations/products?product_id=' + pid
        + '&limit=' + perProduct
        + '&section_id=' + sectionId;

      return fetch(url)
        .then(function (r) {
          if (!r.ok) return '';
          return r.text();
        })
        .catch(function () { return ''; });
    });

    Promise.all(promises).then(function (htmlResults) {
      var seenUrls = {};
      var allSlides = [];

      htmlResults.forEach(function (html) {
        if (!html) return;

        var temp = document.createElement('div');
        temp.innerHTML = html;

        var slides = temp.querySelectorAll('.cart-recs__slide');
        slides.forEach(function (slide) {
          var link = slide.querySelector('a[href]');
          var href = link ? link.getAttribute('href') : '';
          if (href && !seenUrls[href]) {
            seenUrls[href] = true;
            allSlides.push(slide.outerHTML);
          }
        });
      });

      allSlides = allSlides.slice(0, limit);

      if (allSlides.length === 0) {
        container.style.display = 'none';
        return;
      }

      sliderEl.innerHTML = allSlides.join('');

      processInjectedCards(sliderEl);
      initSlick(sliderEl, slidesDesktop, slidesMobile);
    });
  }

  function processInjectedCards(container) {
    fixLazyImages(container);
    wireUpATCButtons(container);
  }

  /**
   * Fix rimg lazy-loaded images.
   * rimg outputs src=real-url but srcset=placeholder-svg.
   * Empire.js IntersectionObserver won't pick up injected images.
   * We swap srcset to real URLs from data-rimg-template.
   */
  function fixLazyImages(container) {
    var lazyImgs = container.querySelectorAll('img[data-rimg="lazy"]');
    lazyImgs.forEach(function (img) {
      var template = img.getAttribute('data-rimg-template');

      if (template) {
        var maxAttr = img.getAttribute('data-rimg-max');
        var maxW = 512;
        if (maxAttr) {
          maxW = parseInt(maxAttr.split('x')[0], 10) || 512;
        }

        var widths = [200, 300, 400, 512];
        var srcset = [];
        widths.forEach(function (w) {
          if (w <= maxW) {
            srcset.push(template.replace('{size}', w + 'x') + ' ' + w + 'w');
          }
        });

        if (srcset.length > 0) {
          img.setAttribute('srcset', srcset.join(', '));
        } else {
          img.setAttribute('srcset', '');
        }
      } else {
        img.setAttribute('srcset', '');
      }

      img.setAttribute('data-rimg', 'loaded');
    });

    container.querySelectorAll('noscript[data-rimg-noscript]').forEach(function (ns) { ns.remove(); });
    container.querySelectorAll('[data-rimg-canvas]').forEach(function (c) { c.remove(); });
  }

  /**
   * Wire up ATC buttons using the same AddToCartFlyout class
   * that Empire.js ProductGridItem uses.
   *
   * For single-variant products: [data-quick-buy] → AddToCartFlyout
   * For multi-variant products: [data-quickshop-slim] → redirect to product page
   */
  function wireUpATCButtons(container) {
    // Get quick-buy settings from any existing script tag on the page,
    // or from the injected cards themselves
    var settingsEl = document.querySelector('[data-quick-buy-settings]')
      || container.querySelector('[data-quick-buy-settings]');
    var settings = { cart_redirection: false, money_format: '' };
    if (settingsEl) {
      try { settings = JSON.parse(settingsEl.textContent); } catch (e) {}
    }

    // Single-variant: data-quick-buy → use AddToCartFlyout (same as Empire.js)
    var quickBuyBtns = container.querySelectorAll('[data-quick-buy]');
    quickBuyBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        if (!window.AddToCartFlyout) {
          // Fallback: simple cart add + redirect
          var vid = btn.getAttribute('data-variant-id');
          if (vid) {
            fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: vid, quantity: 1 })
            }).then(function () {
              window.location.href = window.Theme.routes.cart_url;
            });
          }
          return;
        }

        var variantID = btn.getAttribute('data-variant-id');
        var formData = [
          { name: 'id', value: variantID },
          { name: 'quantity', value: 1 }
        ];
        var options = {
          atcButton: btn,
          settings: {
            moneyFormat: settings.money_format || null,
            cartRedirection: settings.cart_redirection || false
          }
        };

        new window.AddToCartFlyout(formData, options);
      });
    });

    // Multi-variant: data-quickshop-slim → redirect to product page
    var quickShopBtns = container.querySelectorAll('[data-quickshop-slim], [data-quickshop-full]');
    quickShopBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var itemEl = btn.closest('[data-product-quickshop-url]');
        if (itemEl) {
          window.location.href = itemEl.dataset.productQuickshopUrl;
        }
      });
    });
  }

  function initSlick(sliderEl, slidesDesktop, slidesMobile) {
    if (typeof jQuery === 'undefined' || typeof jQuery.fn.slick === 'undefined') {
      setTimeout(function () { initSlick(sliderEl, slidesDesktop, slidesMobile); }, 100);
      return;
    }

    var $slider = jQuery(sliderEl);
    if ($slider.hasClass('slick-initialized')) return;

    $slider.slick({
      slidesToShow: slidesDesktop,
      slidesToScroll: 1,
      infinite: false,
      rtl: true,
      arrows: true,
      dots: false,
      speed: 300,
      prevArrow: '<button type="button" class="cart-recs__arrow cart-recs__arrow--prev" aria-label="הקודם"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg></button>',
      nextArrow: '<button type="button" class="cart-recs__arrow cart-recs__arrow--next" aria-label="הבא"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg></button>',
      responsive: [
        {
          breakpoint: 768,
          settings: {
            slidesToShow: slidesMobile,
            arrows: false
          }
        }
      ]
    });
  }
})();
